// A tiny ring buffer of recent console output, captured so we can attach it to
// user feedback. The user reports "something's broken"; this gives us the last
// few log lines / errors that led up to it without asking them to open devtools.
//
// We patch console.{log,info,warn,error,debug} once at startup, mirror each
// call into a bounded buffer, and always forward to the original method so the
// real console behaviour is untouched.

export interface ConsoleEntry {
  level: "log" | "info" | "warn" | "error" | "debug"
  message: string
  time: string // ISO timestamp
}

const MAX_ENTRIES = 50
const MAX_MESSAGE_LENGTH = 1000

const buffer: ConsoleEntry[] = []
let installed = false

// Best-effort stringify of a single console argument. Errors keep their
// message + stack; objects are JSON-encoded (guarding against cycles); anything
// else falls back to String().
function formatArg(arg: unknown): string {
  if (typeof arg === "string") return arg
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ""}`
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

function record(level: ConsoleEntry["level"], args: unknown[]): void {
  const message = args.map(formatArg).join(" ").slice(0, MAX_MESSAGE_LENGTH)
  buffer.push({ level, message, time: new Date().toISOString() })
  if (buffer.length > MAX_ENTRIES) buffer.shift()
}

export function installConsoleCapture(): void {
  if (installed || typeof console === "undefined") return
  installed = true

  const levels: ConsoleEntry["level"][] = ["log", "info", "warn", "error", "debug"]
  for (const level of levels) {
    const original = console[level]?.bind(console)
    if (!original) continue
    console[level] = (...args: unknown[]) => {
      try {
        record(level, args)
      } catch {
        // Never let capture break logging.
      }
      original(...args)
    }
  }
}

// A snapshot copy of the current buffer (oldest first), safe to attach to a
// payload without exposing the live array.
export function getConsoleSnapshot(): ConsoleEntry[] {
  return buffer.slice()
}
